import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore,
  School,
  Book,
  VideoLibrary,
  Assignment,
  Help,
  DownloadForOffline,
  Article
} from '@mui/icons-material';

const Resource = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const MotionDiv = motion.div;

  const resources = {
    tutorials: [
      {
        title: t('resources.gettingStarted.title'),
        description: t('resources.gettingStarted.description'),
        type: t('resources.type.pdf'),
        icon: <School />,
        link: "https://docs.google.com/document/d/1CU_VlkTmu3zqN6y54MCYPCuDxkbf_dfCIEebT0QQM9g/edit?usp=sharing"
      },
      {
        title: t('resources.videoTutorials.title'),
        description: t('resources.videoTutorials.description'),
        type: t('resources.type.video'),
        icon: <VideoLibrary />,
        link: "https://youtu.be/wLhfk0AMEWo?si=lroNqiTvbRrt4bKr"
      }
    ],
    learningMaterials: [
      {
        title: t('resources.studyGuides.title'),
        description: t('resources.studyGuides.description'),
        type: t('resources.type.document'),
        icon: <Book />,
        link: "https://www.w3schools.com/react/default.asp"
      },
      {
        title: t('resources.practiceTests.title'),
        description: t('resources.practiceTests.description'),
        type: t('resources.type.quiz'),
        icon: <Assignment />,
        link: "https://docs.google.com/forms/d/e/1FAIpQLSfckCPH1Z3XHqFE9gfvQANk0iaD4kdDQGMyahMkfQCx6Tdehw/viewform?usp=sharing"
      }
    ],
    helpCenter: [
      {
        title: t('resources.faqs.title'),
        description: t('resources.faqs.description'),
        icon: <Help />,
        link: "https://docs.google.com/document/d/1sYR-4UqDiPsbpH-iK2HopSq7gDP8t9tYTNfglqgwQiw/edit?usp=sharing"
      },
      {
        title: t('resources.troubleshooting.title'),
        description: t('resources.troubleshooting.description'),
        icon: <Article />,
        link: "https://docs.google.com/document/d/1IDDcbfRghLFxUwGjGrQLDgKRCyO5K7e4TiC8iJfijwc/edit?usp=sharing"
      }
    ]
  };

  const faqs = [
    {
      question: t('resources.faq.addMenu.question'),
      answer: t('resources.faq.addMenu.answer')
    },
    {
      question: t('resources.faq.fileFormats.question'),
      answer: t('resources.faq.fileFormats.answer')
    },
    {
      question: t('resources.faq.salesReport.question'),
      answer: t('resources.faq.salesReport.answer')
    }
  ];

  return (
    <Box>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          {t('resources.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('resources.subtitle')}
        </Typography>
      </MotionDiv>
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 4, background: 'transparent' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {Object.entries(resources).map(([category, items]) => (
                items.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`${category}-${index}`}>
                    <Card sx={{ 
                      height: '100%',
                      borderRadius: 2,
                      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.1)'
                      }
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          {item.icon}
                          <Typography variant="h6" sx={{ ml: 1 }}>
                            {item.title}
                          </Typography>
                        </Box>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                          {item.description}
                        </Typography>
                        {item.type && (
                          <Chip 
                            label={item.type}
                            size="small"
                            color="primary"
                            sx={{ 
                              mb: 2,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white'
                            }}
                          />
                        )}
                        <Button
                          variant="outlined"
                          startIcon={<DownloadForOffline />}
                          fullWidth
                          href={item.link}
                          sx={{
                            borderRadius: 2,
                            borderColor: '#667eea',
                            color: '#667eea',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white'
                            }
                          }}
                        >
                          {t('resources.accessResource')}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ))}
            </Grid>

            <Typography variant="h4" sx={{ mb: 3 }}>
              {t('resources.faqsTitle')}
            </Typography>
            {faqs.map((faq, index) => (
              <Accordion 
                key={index} 
                sx={{ 
                  mb: 1, 
                  borderRadius: 2,
                  '&:before': {
                    display: 'none'
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMore />}
                  sx={{
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.08)'
                    }
                  }}
                >
                  <Typography fontWeight="medium">{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      </MotionDiv>

      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card sx={{ background: 'transparent' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              {t('resources.needHelp.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('resources.needHelp.description')}
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/request-demo')}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                },
                borderRadius: 2
              }}
            >
              {t('resources.contactSupport')}
            </Button>
          </CardContent>
        </Card>
      </MotionDiv>
    </Box>
  );
};

export default Resource;